import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  deleteDoc
} from "firebase/firestore";

export default {
  name: "KnowledgeEditorLogic",
  props: {
    // Renamed to 'initialArticleId' to avoid direct mutation
    initialArticleId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // Local copy for safe modification
      localArticleId: null,

      // Article fields
      title: "",
      category: "",
      createdBy: "",
      description: "",  // Raw EditorJS JSON string (now will be empty on this page)
      timestamp: null,
      lastUpdatedTimestamp: null,

      error: null,
      loading: false,
      successMessage: "",

      // Table of existing articles
      articles: []
    };
  },
  async created() {
    // Copy the prop into our local data property
    this.localArticleId = this.initialArticleId;

    // If we have a localArticleId, fetch that article
    if (this.localArticleId) {
      await this.fetchArticle(this.localArticleId);
    }

    // Also fetch the list of articles for the table
    await this.fetchArticles();
  },
  methods: {
    // Fetch a single article for editing
    async fetchArticle(articleId) {
      this.loading = true;
      try {
        const articleRef = doc(db, "knowledgeBlog", articleId);
        const docSnap = await getDoc(articleRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          this.title = data.title || "";
          this.category = data.category || "";
          this.createdBy = data.createdBy || "";
          this.description = data.description || "";
          this.timestamp = data.timestamp;
          this.lastUpdatedTimestamp = data.lastUpdatedTimestamp;
        } else {
          this.error = "Article not found.";
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        this.error = "Error fetching article.";
      } finally {
        this.loading = false;
      }
    },

    // Create or update an article
    async saveArticle() {
      this.loading = true;
      try {
        const user = this.$root.$data.currentUser;
        const createdByName = user && user.displayName ? user.displayName : "Anonymous";

        if (this.localArticleId) {
          // Update existing article
          const articleRef = doc(db, "knowledgeBlog", this.localArticleId);
          await updateDoc(articleRef, {
            title: this.title,
            category: this.category,
            description: this.description,
            lastUpdatedTimestamp: serverTimestamp()
          });
          this.successMessage = "Article updated!";
        } else {
          // Create new article
          const articlesRef = collection(db, "knowledgeBlog");
          const docRef = await addDoc(articlesRef, {
            title: this.title,
            category: this.category,
            createdBy: createdByName,
            description: this.description,
            timestamp: serverTimestamp(),
            lastUpdatedTimestamp: serverTimestamp()
          });
          this.localArticleId = docRef.id;
          this.successMessage = "Article created!";
        }

        await this.fetchArticles();
      } catch (err) {
        console.error("Error saving article:", err);
        this.error = "Error saving article.";
      } finally {
        this.loading = false;
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      }
    },

    async handleSave() {
      // We're not using any editor on this page so clear the description
      this.description = "";
      await this.saveArticle();
      // After saving, navigate to the dedicated Article Editor page using the localArticleId
      this.$router.push(`/knowledge-editor/edit/${this.localArticleId}`);
    },

    // Fetch all articles for the table
    async fetchArticles() {
      try {
        const articlesRef = collection(db, "knowledgeBlog");
        const snapshot = await getDocs(articlesRef);
        const articlesList = [];

        snapshot.forEach(docSnap => {
          articlesList.push({
            id: docSnap.id,
            ...docSnap.data()
          });
        });

        this.articles = articlesList;
      } catch (err) {
        console.error("Error fetching articles:", err);
        this.error = "Error fetching articles.";
      }
    },

    // Delete an article from Firestore
    async deleteArticle(article) {
      const confirmDelete = window.confirm("Are you sure you want to delete this article?");
      if (!confirmDelete) return;
      try {
        const articleRef = doc(db, "knowledgeBlog", article.id);
        await deleteDoc(articleRef);
        this.articles = this.articles.filter(a => a.id !== article.id);
        this.successMessage = "Article deleted.";
        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (err) {
        console.error("Error deleting article:", err);
        this.error = "Failed to delete article.";
      }
    },

    // Navigate to the dedicated Article Editor route for editing
    editArticle(article) {
      this.$router.push(`/knowledge-editor/edit/${article.id}`);
    }
  }
};
